import { useState, useMemo, useCallback, useRef } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, Check, Trash2, Download } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime, getFileName, getStatusColor } from '@har-insight/parser';
import { exportAsCSV } from '@har-insight/parser';
import { showToast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';

type SortField = 'status' | 'method' | 'name' | 'size' | 'time';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

function SortIcon({ field, sortConfig }: { field: SortField; sortConfig: SortConfig | null }) {
  if (!sortConfig || sortConfig.field !== field) {
    return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  }
  return sortConfig.direction === 'asc' ? (
    <ArrowUp className="w-3 h-3" />
  ) : (
    <ArrowDown className="w-3 h-3" />
  );
}

export function RequestTable() {
  const { filteredEntries, selectedEntry, selectEntry } = useHARStore();
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastSelectedIndex = useRef<number>(-1);

  const sortedEntries = useMemo(() => {
    if (!sortConfig) return filteredEntries;

    return [...filteredEntries].sort((a, b) => {
      let comparison = 0;

      switch (sortConfig.field) {
        case 'status':
          comparison = a.response.status - b.response.status;
          break;
        case 'method':
          comparison = a.request.method.localeCompare(b.request.method);
          break;
        case 'name':
          comparison = getFileName(a.request.url).localeCompare(getFileName(b.request.url));
          break;
        case 'size':
          comparison = (a.response.content.size || 0) - (b.response.content.size || 0);
          break;
        case 'time':
          comparison = (a.time || 0) - (b.time || 0);
          break;
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredEntries, sortConfig]);

  const handleSort = (field: SortField) => {
    setSortConfig(current => {
      if (!current || current.field !== field) {
        return { field, direction: 'desc' };
      }
      if (current.direction === 'desc') {
        return { field, direction: 'asc' };
      }
      return null; // Clear sort
    });
  };

  const handleRowClick = useCallback((entry: typeof sortedEntries[0], index: number, event: React.MouseEvent) => {
    // Handle Ctrl/Cmd + Click for multi-select
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(entry._id || '')) {
          newSet.delete(entry._id || '');
        } else {
          newSet.add(entry._id || '');
        }
        return newSet;
      });
      lastSelectedIndex.current = index;
      return;
    }

    // Handle Shift + Click for range selection
    if (event.shiftKey) {
      event.preventDefault();
      const start = Math.min(lastSelectedIndex.current, index);
      const end = Math.max(lastSelectedIndex.current, index);
      
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        for (let i = start; i <= end; i++) {
          if (i >= 0 && i < sortedEntries.length) {
            newSet.add(sortedEntries[i]._id || '');
          }
        }
        return newSet;
      });
      return;
    }

    // Normal click - clear selection and select single entry
    setSelectedIds(new Set([entry._id || '']));
    lastSelectedIndex.current = index;
    selectEntry(entry);
  }, [sortedEntries, selectEntry]);

  const handleExportSelected = useCallback(() => {
    const selectedEntries = sortedEntries.filter(e => selectedIds.has(e._id || ''));
    if (selectedEntries.length === 0) {
      showToast('No entries selected', 'warning');
      return;
    }

    const content = exportAsCSV(selectedEntries);
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export-${selectedEntries.length}-selected.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    showToast(`Exported ${selectedEntries.length} selected entries`, 'success');
  }, [sortedEntries, selectedIds]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastSelectedIndex.current = -1;
  }, []);

  const hasSelection = selectedIds.size > 0;

  if (filteredEntries.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-slate-400 dark:text-slate-500">
        No requests match your filters
      </div>
    );
  }

  const headerClass = cn(
    'table-cell text-slate-500 dark:text-slate-400 font-medium cursor-pointer select-none',
    'hover:text-slate-700 dark:hover:text-slate-200 transition-colors'
  );

  return (
    <div className="flex flex-col h-full">
      {/* Selection toolbar */}
      {hasSelection && (
        <div className="flex items-center justify-between p-2 mb-2 bg-brand-50 dark:bg-brand-500/10 rounded-lg border border-brand-200 dark:border-brand-500/30 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-brand-600 dark:text-brand-400" />
            <span className="text-sm font-medium text-brand-700 dark:text-brand-300">
              {selectedIds.size} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportSelected}
              className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:bg-brand-100 dark:hover:bg-brand-500/20 rounded transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Export CSV
            </button>
            <button
              onClick={handleClearSelection}
              className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="overflow-auto flex-1 max-h-[600px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 dark:bg-slate-900 z-10">
            <tr className="border-b border-slate-200 dark:border-slate-800">
              <th className="table-cell text-slate-400 dark:text-slate-500 font-medium w-10 text-center">
                #
              </th>
              <th
                className={cn(headerClass, 'text-left w-16')}
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">
                  Status
                  <SortIcon field="status" sortConfig={sortConfig} />
                </div>
              </th>
              <th
                className={cn(headerClass, 'text-left w-16')}
                onClick={() => handleSort('method')}
              >
                <div className="flex items-center gap-1">
                  Method
                  <SortIcon field="method" sortConfig={sortConfig} />
                </div>
              </th>
              <th
                className={cn(headerClass, 'text-left')}
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Name
                  <SortIcon field="name" sortConfig={sortConfig} />
                </div>
              </th>
              <th
                className={cn(headerClass, 'text-right w-20')}
                onClick={() => handleSort('size')}
              >
                <div className="flex items-center justify-end gap-1">
                  Size
                  <SortIcon field="size" sortConfig={sortConfig} />
                </div>
              </th>
              <th
                className={cn(headerClass, 'text-right w-20')}
                onClick={() => handleSort('time')}
              >
                <div className="flex items-center justify-end gap-1">
                  Time
                  <SortIcon field="time" sortConfig={sortConfig} />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEntries.map((entry, index) => {
              const isSelected = selectedIds.has(entry._id || '');
              const isActive = selectedEntry?._id === entry._id;
              const fileName = getFileName(entry.request.url);
              const statusColor = getStatusColor(entry.response.status);
              // Extract sequence number from _id (format: "entry-N")
              const seqNum = entry._id ? parseInt(entry._id.split('-')[1], 10) + 1 : index + 1;

              return (
                <tr
                  key={entry._id || index}
                  onClick={(e) => handleRowClick(entry, index, e)}
                  className={cn(
                    'table-row cursor-pointer transition-colors',
                    isSelected && 'bg-brand-500/10',
                    isActive && 'border-brand-500/30',
                    !isSelected && 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  )}
                >
                  <td className={cn(
                    'table-cell text-center text-xs font-mono',
                    isSelected ? 'text-brand-600 dark:text-brand-400 font-bold' : 'text-slate-400 dark:text-slate-500'
                  )}>
                    {isSelected ? <Check className="w-3.5 h-3.5 inline" /> : seqNum}
                  </td>
                  <td className="table-cell">
                    <span className={cn('font-mono', statusColor)}>
                      {entry.response.status}
                    </span>
                  </td>
                  <td className="table-cell">
                    <span className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-800 rounded text-xs font-medium">
                      {entry.request.method}
                    </span>
                  </td>
                  <td className="table-cell">
                    <div className="flex flex-col">
                      <span className="text-slate-800 dark:text-slate-200 truncate max-w-[300px]" title={entry.request.url}>
                        {fileName}
                      </span>
                      <span className="text-xs text-slate-400 dark:text-slate-600 truncate max-w-[300px]">
                        {new URL(entry.request.url).hostname}
                      </span>
                    </div>
                  </td>
                  <td className="table-cell text-right text-slate-500 dark:text-slate-400">
                    {formatBytes(entry.response.content.size || 0)}
                  </td>
                  <td className="table-cell text-right">
                    <span className={cn(
                      'font-mono',
                      entry.time > 1000 ? 'text-yellow-600 dark:text-yellow-400' : 'text-slate-500 dark:text-slate-400',
                      entry.time > 3000 && 'text-red-600 dark:text-red-400'
                    )}>
                      {formatTime(entry.time || 0)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Multi-select hint */}
      <div className="mt-2 text-xs text-slate-400 dark:text-slate-600 text-center">
        <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded text-[10px]">Ctrl/Cmd</kbd> + Click to multi-select • 
        <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded text-[10px]">Shift</kbd> + Click for range
      </div>
    </div>
  );
}
